import type { HeadquartersSite } from './headquarters-overview-read';
import type { HqHistoricalSummary } from '@kop/contracts';

export function headquartersInspectionTotal(sites: readonly HeadquartersSite[], month: string) {
  if (!sites.length || sites.some(site => !site.inspection || site.inspection.month !== month)) return null;
  return sites.reduce((sum, site) => ({ reports: sum.reports + site.inspection!.reports,
    findings: sum.findings + site.inspection!.findings, completed: sum.completed + site.inspection!.completed,
    unresolved: sum.unresolved + site.inspection!.unresolved, carriedUnresolved: sum.carriedUnresolved + site.inspection!.carriedUnresolved,
  }), { reports: 0, findings: 0, completed: 0, unresolved: 0, carriedUnresolved: 0 });
}

export function headquartersInspectionRate(value: { findings: number; completed: number } | null | undefined) {
  return !value || value.findings === 0 ? null : value.completed / value.findings * 100;
}

export function headquartersHalfyearInspectionTotal(sites: readonly HeadquartersSite[], year: number, half: 1 | 2) {
  if (!sites.length || sites.some(site => !site.halfyearInspection || site.halfyearInspection.year !== year || site.halfyearInspection.half !== half)) return null;
  return sites.reduce((sum, site) => ({ reports: sum.reports + site.halfyearInspection!.reports,
    findings: sum.findings + site.halfyearInspection!.findings, completed: sum.completed + site.halfyearInspection!.completed,
    unresolved: sum.unresolved + site.halfyearInspection!.unresolved, carriedUnresolved: sum.carriedUnresolved + site.halfyearInspection!.carriedUnresolved,
  }), { reports: 0, findings: 0, completed: 0, unresolved: 0, carriedUnresolved: 0 });
}

export type HeadquartersCalendarEntry = Readonly<{ key: string; date: string; title: string; reportId: string; page: 'meetings' | 'inspections' }>;
/** Read saved dates only. An undated report or a reported result never invents a planned visit. */
export function headquartersCalendarEntries(summary: HqHistoricalSummary | null | undefined, month: string): HeadquartersCalendarEntry[] {
  if (!summary || !/^\d{4}-(0[1-9]|1[0-2])$/u.test(month)) return [];
  const rows: HeadquartersCalendarEntry[] = [];
  for (const meeting of summary.monthlyMeetings) if (meeting.meetingDate?.startsWith(`${month}-`))
    rows.push({ key: meeting.id, date: meeting.meetingDate, title: `${meeting.round}차 월회의`, reportId: meeting.id, page: 'meetings' });
  const days = new Date(Number(month.slice(0, 4)), Number(month.slice(5)), 0).getDate();
  for (const report of summary.inspections) for (const [kind, from, to] of [
    ['예정', report.plannedFrom, report.plannedTo], ['실시', report.actualFrom, report.actualTo],
  ] as const) {
    if (!from || (to && to < from)) continue;
    for (let day = 1; day <= days; day++) {
      const date = `${month}-${String(day).padStart(2, '0')}`;
      if (date < from || date > (to ?? from)) continue;
      rows.push({ key: `${report.id}:${kind}:${date}`, date, title: `${report.title} · ${kind}`, reportId: report.id, page: 'inspections' });
    }
  }
  return rows.sort((a, b) => a.date.localeCompare(b.date) || a.key.localeCompare(b.key));
}
